import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Paper, Typography, Button, Stack, Table, TableHead, TableRow, TableCell,
  TableBody, Chip, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Alert, FormControl, InputLabel, Select, MenuItem, Tooltip,
} from "@mui/material";
import { Add, LockReset, Block, CheckCircle } from "@mui/icons-material";
import { listUsers, createUser, updateUser, listGroups, UserRow } from "../../api";
import { useAuth } from "../../auth/AuthContext";
import GroupPicker from "../../components/GroupPicker";

export default function UsersAdmin() {
  const { isSuperAdmin, me } = useAuth();
  const [rows, setRows] = useState<UserRow[]>([]);
  const [groupOpts, setGroupOpts] = useState<{ id: number; name: string }[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [secret, setSecret] = useState<{ username: string; pw: string } | null>(null);

  const [creating, setCreating] = useState(false);
  const [nu, setNu] = useState({ username: "", password: "", role: "user", group_ids: [] as number[] });
  const [editGroups, setEditGroups] = useState<UserRow | null>(null);
  const [editIds, setEditIds] = useState<number[]>([]);

  const load = useCallback(async () => {
    setErr(null);
    try { setRows(await listUsers()); } catch (e: any) { setErr(e.message); }
    if (isSuperAdmin) {
      try { const g = await listGroups(); setGroupOpts(g.map((x) => ({ id: x.id, name: x.name }))); } catch { /* ignore */ }
    } else {
      setGroupOpts((me?.groups || []).filter((g) => g.role === "admin").map((g) => ({ id: g.id, name: g.name })));
    }
  }, [isSuperAdmin, me]);
  useEffect(() => { load(); }, [load]);

  const doCreate = async () => {
    setErr(null);
    try {
      const res = await createUser({
        username: nu.username.trim(),
        password: nu.password || undefined,
        role: isSuperAdmin ? nu.role : undefined,
        group_ids: nu.group_ids,
      });
      setCreating(false);
      setNu({ username: "", password: "", role: "user", group_ids: [] });
      if (res.temp_password) setSecret({ username: res.username, pw: res.temp_password });
      await load();
    } catch (e: any) { setErr(e.message); }
  };

  const toggleEnabled = async (u: UserRow) => {
    setErr(null);
    try { await updateUser(u.id, { enabled: !u.enabled }); await load(); } catch (e: any) { setErr(e.message); }
  };
  const resetPw = async (u: UserRow) => {
    if (!window.confirm(`Reset ${u.username}'s password to a new temporary one?`)) return;
    setErr(null);
    try {
      const res = await updateUser(u.id, { reset_password: true });
      if (res.temp_password) setSecret({ username: u.username, pw: res.temp_password });
      await load();
    } catch (e: any) { setErr(e.message); }
  };
  const openEditGroups = (u: UserRow) => { setEditGroups(u); setEditIds(u.groups.map((g) => g.id)); };
  const saveGroups = async () => {
    if (!editGroups) return;
    setErr(null);
    try { await updateUser(editGroups.id, { group_ids: editIds }); setEditGroups(null); await load(); }
    catch (e: any) { setErr(e.message); }
  };

  return (
    <Box>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h4">Users</Typography>
          <Button variant="contained" startIcon={<Add />} onClick={() => setCreating(true)}>New user</Button>
        </Stack>
        {err && <Alert severity="error" sx={{ mt: 2 }}>{err}</Alert>}
      </Paper>

      <Paper>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Username</TableCell><TableCell>Role</TableCell><TableCell>Status</TableCell>
              <TableCell>Groups</TableCell><TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((u) => (
              <TableRow key={u.id} hover>
                <TableCell>
                  {u.username}
                  {u.must_change_password && <Chip size="small" color="warning" label="temp pw" sx={{ ml: 1 }} />}
                </TableCell>
                <TableCell>
                  <Chip size="small" color={u.role === "admin" ? "secondary" : "default"} label={u.role === "admin" ? "super-admin" : "user"} />
                </TableCell>
                <TableCell>{u.enabled
                  ? <Chip size="small" color="success" label="enabled" />
                  : <Chip size="small" color="error" label="disabled" />}</TableCell>
                <TableCell>
                  <Stack direction="row" gap={0.5} flexWrap="wrap">
                    {u.groups.map((g) => <Chip key={g.id} size="small" label={`${g.name}${g.role === "admin" ? " (admin)" : ""}`} />)}
                  </Stack>
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" title="Edit groups" onClick={() => openEditGroups(u)}><Add fontSize="small" /></IconButton>
                  <IconButton size="small" title="Reset password" onClick={() => resetPw(u)}><LockReset fontSize="small" /></IconButton>
                  <Tooltip title={u.enabled ? "Disable" : "Enable"}>
                    <IconButton size="small" onClick={() => toggleEnabled(u)}>
                      {u.enabled ? <Block fontSize="small" /> : <CheckCircle fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4 }}>No users.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Paper>

      {/* Create */}
      <Dialog open={creating} onClose={() => setCreating(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New user</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Username" fullWidth value={nu.username} onChange={(e) => setNu({ ...nu, username: e.target.value })} autoFocus />
            <TextField label="Password (blank = generate temp)" fullWidth value={nu.password}
              onChange={(e) => setNu({ ...nu, password: e.target.value })}
              helperText="Leave blank to generate a one-time temp password (user must change it on first login)." />
            {isSuperAdmin && (
              <FormControl fullWidth size="small">
                <InputLabel>Role</InputLabel>
                <Select label="Role" value={nu.role} onChange={(e) => setNu({ ...nu, role: e.target.value })}>
                  <MenuItem value="user">user</MenuItem>
                  <MenuItem value="admin">super-admin</MenuItem>
                </Select>
              </FormControl>
            )}
            <GroupPicker label="Groups" options={groupOpts} value={nu.group_ids} onChange={(v) => setNu({ ...nu, group_ids: v })} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreating(false)}>Cancel</Button>
          <Button variant="contained" onClick={doCreate} disabled={!nu.username.trim()}>Create</Button>
        </DialogActions>
      </Dialog>

      {/* Edit groups */}
      <Dialog open={!!editGroups} onClose={() => setEditGroups(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Groups for {editGroups?.username}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <GroupPicker label="Groups" options={groupOpts} value={editIds} onChange={setEditIds} />
            {!isSuperAdmin && <Typography variant="caption" color="text.secondary">You can only change membership in groups you administer.</Typography>}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditGroups(null)}>Cancel</Button>
          <Button variant="contained" onClick={saveGroups}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* One-time secret */}
      <Dialog open={!!secret} onClose={() => setSecret(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Temporary password</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Share this with <b>{secret?.username}</b> now. It is shown once and they must change it on first login.
          </Typography>
          <TextField fullWidth value={secret?.pw || ""} InputProps={{ readOnly: true }} onFocus={(e) => e.target.select()} />
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setSecret(null)}>Done</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
